import { formatServerError } from "./errors"
import { useDialog } from "@opencode/ui/context/dialog"
import { Menu } from "@opencode/ui/menu"
import { WorkspaceDetailsDialog } from "./workspace-details"
import { Persistence } from "@/runtime/persistence/schema"
import { Schema } from "effect"
import { Persist, persisted } from "@/runtime/persistence/storage"
import { createSimpleContext } from "@opencode/ui/context"
import { createEffect, onCleanup, Show } from "solid-js"
import { createStore } from "solid-js/store"
import { useLocation, useNavigate } from "@solidjs/router"
import type { RemoteWorkspace } from "@opencode/schema/remote-workspace"
import { ServerConnection, useServers } from "./registry"
import { useGlobal } from "./runtime"
import { tabHref, useTabs } from "@/shell/tabs/tabs"
import { useLanguage } from "@/runtime/i18n/language"
import { useCloudWorkspace } from "./cloud-context"

export const { use: useRemoteWorkspace, provider: RemoteWorkspaceProvider } = createSimpleContext({
  name: "RemoteWorkspace",
  init: () => {
    const cloud = useCloudWorkspace()
    if (cloud) return cloud
    const servers = useServers()
    const global = useGlobal()
    const tabs = useTabs()
    const navigate = useNavigate()
    const location = useLocation()
    const language = useLanguage()
    const [preference, setPreference, , preferenceReady] = persisted(
      Persist.window("remote-workspace"),
      Persistence.struct({
        remote: Schema.Boolean,
        subject: Schema.optional(Schema.String),
        audience: Schema.optional(Schema.String),
      }),
      { remote: false },
    )
    let restored = false
    let clientID = crypto.randomUUID()
    const requested = new Set<string>()
    const local = () => servers.list.find(ServerConnection.local)
    const ctx = () => {
      const conn = local()
      return conn ? global.ensureServerCtx(conn).account : undefined
    }
    const control = () => {
      const conn = local()
      return conn ? global.ensureServerCtx(conn).sdk.api : undefined
    }
    const [state, setState] = createStore<{
      busy: boolean
      phase: string
      error?: string
      connection?: RemoteWorkspace.Connection
    }>({ busy: false, phase: "local" })
    let attempt: AbortController | undefined
    let account: { subject: string; audience: string } | undefined
    let lease = new AbortController()
    let connecting: Promise<RemoteWorkspace.Connection> | undefined
    let refreshing: Promise<RemoteWorkspace.Connection> | undefined
    let active = false
    const selected = () =>
      tabs.store.some(
        (tab) =>
          tab.server === `remote:${state.connection?.key}` && tabHref(tab) === location.pathname + location.search,
      )
    const activity = () => {
      if (!state.connection || !selected()) return
      active = true
      if (["stopped", "stopping"].includes(state.phase) && !state.busy) void ensureRemote().catch(() => undefined)
    }
    window.addEventListener("keydown", activity, { passive: true })
    window.addEventListener("pointerdown", activity, { passive: true })
    const release = (id = clientID) => {
      if (!requested.delete(id)) return
      if (id === clientID) clientID = crypto.randomUUID()
      // Lease cleanup must not keep a cancelled UI attempt alive.
      void control()
        ?.remoteWorkspace.disconnect({ clientID: id }, { signal: AbortSignal.timeout(25_000) })
        .catch(() => undefined)
    }
    const acquire = async (signal: AbortSignal, wake = false, id = clientID) => {
      const api = control()
      if (!api) throw new Error(language.t("remote.localUnavailable"))
      while (!signal.aborted) {
        requested.add(id)
        const status = await api.remoteWorkspace.connect({ clientID: id, active, wake }, { signal })
        signal.throwIfAborted()
        active = false
        setState("phase", status.state)
        if (status.connection) return status.connection
        if (!wake && ["stopped", "stopping", "unallocated"].includes(status.state))
          throw new Error(language.t("remote.sleeping"))
        if (["failed", "unavailable"].includes(status.state)) throw new Error(language.t("remote.failed"))
        await new Promise<void>((resolve, reject) => {
          const done = () => {
            clearTimeout(timer)
            signal.removeEventListener("abort", cancel)
            resolve()
          }
          const cancel = () => {
            clearTimeout(timer)
            signal.removeEventListener("abort", cancel)
            reject(signal.reason)
          }
          const timer = setTimeout(done, 1000)
          signal.addEventListener("abort", cancel, { once: true })
        })
      }
      throw signal.reason
    }
    const register = (connection: RemoteWorkspace.Connection) => {
      const status = ctx()?.state.status
      const profile = status?.profile
      if (
        status?.auth !== "authenticated" ||
        profile?.sairId !== connection.subject ||
        profile.remoteWorkspace?.audience !== connection.audience
      )
        throw new Error(language.t("remote.accountChanged"))
      setState("connection", connection)
      servers.setRemote({
        type: "remote",
        enabled: () => state.phase === "ready",
        id: connection.key,
        displayName: language.t("remote.remote"),
        http: { url: connection.url, password: connection.password },
        reconnect: async (signal) => {
          const next = await renew(signal)
          return { url: next.url, password: next.password }
        },
      })
    }
    function renew(signal: AbortSignal) {
      if (refreshing) return refreshing
      const combined = AbortSignal.any([signal, lease.signal])
      const pending = acquire(combined)
        .then((conn) => {
          if (state.connection) register(conn)
          return conn
        })
        .catch((error) => {
          if (!combined.aborted && !["stopped", "stopping"].includes(state.phase)) setState("phase", "unavailable")
          throw error
        })
        .finally(() => {
          if (refreshing === pending) refreshing = undefined
        })
      refreshing = pending
      return refreshing
    }
    const disconnect = () => {
      const id = clientID
      lease.abort()
      attempt?.abort()
      connecting = undefined
      refreshing = undefined
      account = undefined
      active = false
      if (selected()) navigate("/?view=library")
      setPreference("remote", false)
      setState({ connection: undefined, busy: false, phase: "local", error: undefined })
      servers.setRemote(undefined)
      release(id)
    }
    createEffect(() => {
      const status = ctx()?.state.status
      if (!state.connection && !state.busy) return
      const identity = state.connection ?? account
      if (
        (status?.auth !== "authenticated" && status?.auth !== "unavailable") ||
        (status?.profile &&
          (status.profile.sairId !== identity?.subject ||
            status.profile.remoteWorkspace?.audience !== identity?.audience))
      )
        void disconnect()
    })
    const timer = setInterval(() => {
      if (!state.connection || state.busy || refreshing) return
      void renew(AbortSignal.timeout(25_000))
        .then(() => setState("error", undefined))
        .catch(() =>
          setState(
            "error",
            ["stopped", "stopping"].includes(state.phase) ? undefined : language.t("remote.reconnecting"),
          ),
        )
    }, 20_000)
    onCleanup(() => {
      clearInterval(timer)
      window.removeEventListener("keydown", activity)
      window.removeEventListener("pointerdown", activity)
      attempt?.abort()
      lease.abort()
      void release()
    })
    function ensureRemote(caller?: AbortSignal, force = false): Promise<RemoteWorkspace.Connection> {
      const status = ctx()?.state.status
      if (status?.auth !== "authenticated" || !status.profile?.remoteWorkspace)
        return Promise.reject(new Error(language.t("remote.unavailable")))
      if (caller?.aborted) return Promise.reject(caller.reason)
      if (
        state.connection &&
        (state.connection.subject !== status.profile.sairId ||
          state.connection.audience !== status.profile.remoteWorkspace.audience)
      )
        return Promise.reject(new Error(language.t("remote.accountChanged")))
      if (state.connection && state.phase === "ready" && !force) return Promise.resolve(state.connection)
      if (connecting) return connecting
      account = { subject: status.profile.sairId, audience: status.profile.remoteWorkspace.audience }
      if (lease.signal.aborted) lease = new AbortController()
      // A delayed release for the previous attempt must never release a new lease.
      if (!state.connection || !requested.has(clientID)) clientID = crypto.randomUUID()
      const id = clientID
      const abort = new AbortController()
      attempt = abort
      const timeout = AbortSignal.timeout(300_000)
      const signal = AbortSignal.any([lease.signal, abort.signal, timeout, ...(caller ? [caller] : [])])
      setState({ busy: true, phase: "connecting", error: undefined })
      const pending = (async () => {
        try {
          const conn = await acquire(signal, true, id)
          signal.throwIfAborted()
          register(conn)
          setPreference({ remote: true, subject: conn.subject, audience: conn.audience })
          return conn
        } catch (error) {
          if (attempt === abort && !abort.signal.aborted && !lease.signal.aborted && !caller?.aborted)
            setState(
              "error",
              timeout.aborted
                ? language.t("remote.startup.timeout")
                : formatServerError(error, language.t, language.t("remote.failed")),
            )
          release(id)
          throw error
        } finally {
          if (attempt === abort) {
            setState("busy", false)
            attempt = undefined
            connecting = undefined
          }
        }
      })()
      connecting = pending
      return connecting
    }
    createEffect(() => {
      const profile = ctx()?.state.status?.profile
      if (!preferenceReady() || restored || !profile) return
      restored = true
      if (
        preference.remote &&
        preference.subject === profile.sairId &&
        preference.audience === profile.remoteWorkspace?.audience &&
        profile.remoteWorkspace
      )
        void ensureRemote().catch(() => undefined)
    })
    const currentControl = (key: string) => {
      if (state.connection?.key !== key) throw new Error(language.t("remote.accountChanged"))
      const api = control()
      if (!api) throw new Error(language.t("remote.localUnavailable"))
      return api
    }
    return {
      state,
      available: () => ctx()?.state.status?.auth === "authenticated" && !!ctx()?.state.status?.profile?.remoteWorkspace,
      ensureRemote,
      details: (key: string) => currentControl(key).remoteWorkspace.details({ signal: AbortSignal.timeout(25_000) }),
      sshKey: async (key: string) => {
        const connection = state.connection
        const result = await currentControl(key).remoteWorkspace.sshKey({ signal: AbortSignal.timeout(25_000) })
        if (state.connection?.key !== key || result.filename !== `${connection?.workspaceID}.key`)
          throw new Error(language.t("remote.accountChanged"))
        return result
      },
      resize: async (key: string, input: RemoteWorkspace.Resize) => {
        await currentControl(key).remoteWorkspace.resize(input, { signal: AbortSignal.timeout(25_000) })
        if (state.connection?.key === key) setState("phase", "resizing")
      },
    }
  },
})

export function RemoteWorkspaceMenuItem(props: { server?: ServerConnection.Any }) {
  const dialog = useDialog()
  const remote = useRemoteWorkspace()
  const language = useLanguage()
  const key = () => (props.server?.type === "remote" ? props.server.id : undefined)
  return (
    <Show when={key() && key() === remote.state.connection?.key}>
      <Menu.Item
        data-action="remote-workspace-details"
        onSelect={() => {
          const connectionKey = key()
          if (connectionKey) void dialog.push(() => <WorkspaceDetailsDialog connectionKey={connectionKey} />)
        }}
      >
        {language.t("remote.details.title")}
      </Menu.Item>
    </Show>
  )
}
